import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { useVipStore } from "../context/VipContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 10;

export default function VipNotification() {
  const { selectedVip } = useVipStore();
  const vipId = selectedVip?._id || selectedVip?.id;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const notificationSound = useRef(new Audio("/notify.mp3"));
  const prevUnreadCount = useRef(0);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 991;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchNotifications = async () => {
    if (!vipId) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("vipToken");

      const res = await axios.get(
        `${BASE_URL}/api/notification/${vipId}/vip`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sortedData = Array.isArray(res.data)
        ? [...res.data].reverse()
        : [];

      setNotifications(sortedData);
    } catch (err) {
      console.error("VIP Notification fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!vipId) return;
    setNotifications([]);
    setCurrentPage(1);
    fetchNotifications();
  }, [vipId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      notificationSound.current.play().catch(() => {});
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  const filteredData = useMemo(() => {
    const text = search.trim().toLowerCase();

    return notifications.filter((n) => {
      const searchMatch = text
        ? [n.message || n.notificationMessage, n.vip?.name, n.read ? "read" : "unread"].some(
            (field) => String(field || "").toLowerCase().includes(text)
          )
        : true;

      const statusMatch = statusFilter
        ? statusFilter === "unread"
          ? !n.read
          : n.read
        : true;

      return searchMatch && statusMatch;
    });
  }, [notifications, search, statusFilter]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    return isNaN(d)
      ? "--"
      : d.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("vipToken");
      await axios.get(`${BASE_URL}/api/notification/read/vip/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notificationSound.current.play().catch(() => {});
      fetchNotifications();
    } catch (err) {
      console.error("VIP mark read failed", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    const token = localStorage.getItem("vipToken");
    const unread = notifications.filter((n) => !n.read);

    try {
      await Promise.all(
        unread.map((n) =>
          axios.get(`${BASE_URL}/api/notification/read/all/vip/${n.notificationId || n.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      notificationSound.current.play().catch(() => {});
      fetchNotifications();
    } catch (err) {
      console.error("VIP mark all read failed", err);
    }
  };

  const handleRefresh = () => {
    fetchNotifications();
  };

  if (!vipId) {
    return (
      <div className="no-vip-card">
        <h3>No VIP Selected</h3>
      </div>
    );
  }

  return (
    <>
      <div className="notification-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Notifications</h1>
              <p>Stay updated with real-time security alerts</p>
            </div>
            <div className="unread-badge">
              <i className="fas fa-bell"></i>
              <span>{unreadCount} Unread</span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="page-main">
          {/* Filters & Actions */}
          <section className="controls-card">
            <div className="controls-grid">
              {/* Search */}
              <div className="search-container">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="search-input"
                />
              </div>

              {/* Status Filter */}
              <div className="filter-container">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-select"
                >
                  <option value="">All Status</option>
                  <option value="read">Read</option>
                  <option value="unread">Unread</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button onClick={handleRefresh} className="refresh-btn">
                  <i className="fas fa-sync-alt"></i> Refresh
                </button>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllAsRead} className="mark-all-btn">
                    <i className="fas fa-check-double"></i> Mark All Read
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Notifications List */}
          <section className="notifications-card">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="no-data-card">
                <i className="fas fa-bell-slash fa-3x"></i>
                <h3>No notifications found</h3>
              </div>
            ) : (
              <>
                {isMobile ? (
                  /* Mobile Card View */
                  <div className="mobile-notifications">
                    {paginatedData.map((item, index) => (
                      <div
                        key={item.notificationId || item.id}
                        className={`mobile-notification-card ${item.read ? "read" : "unread"}`}
                      >
                        <div className="notification-header">
                          <div className="notification-title">
                            <strong>#{ (currentPage - 1) * ITEMS_PER_PAGE + index + 1 }</strong>
                            {!item.read && <span className="unread-dot"></span>}
                          </div>
                          <div className="notification-time">
                            {formatDate(item.notificationAssignTime)}
                          </div>
                        </div>
                        <div className="notification-message">
                          {item.notificationMessage || item.message}
                        </div>
                        <div className="notification-footer">
                          <div className="read-time">
                            Read: {formatDate(item.notificationReadTime)}
                          </div>
                          {!item.read && (
                            <button
                              onClick={() => handleMarkAsRead(item.notificationId || item.id)}
                              className="mark-read-btn"
                            >
                              Mark as Read
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Desktop Table View */
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Message</th>
                          <th>Received</th>
                          <th>Read Time</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item, index) => (
                          <tr
                            key={item.notificationId || item.id}
                            className={item.read ? "read-row" : "unread-row"}
                          >
                            <td>
                              <strong>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</strong>
                            </td>
                            <td className="message-cell">
                              {!item.read && <span className="unread-dot"></span>}
                              {item.notificationMessage || item.message}
                            </td>
                            <td>{formatDate(item.notificationAssignTime)}</td>
                            <td>{formatDate(item.notificationReadTime)}</td>
                            <td>
                              <span className={`status-badge ${item.read ? "read" : "unread"}`}>
                                {item.read ? "Read" : "Unread"}
                              </span>
                            </td>
                            <td>
                              {!item.read && (
                                <button
                                  onClick={() => handleMarkAsRead(item.notificationId || item.id)}
                                  className="mark-read-btn"
                                >
                                  Mark Read
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="page-btn"
                    >
                      <i className="fas fa-chevron-left"></i> Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                      >
                        {i + 1}
                      </button>
                    ))}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="page-btn"
                    >
                      Next <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {/* Beautiful Light Purple Glassmorphic Theme */}
      <style jsx>{`
        .notification-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          color: #4c1d95;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(168, 85, 247, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .unread-badge {
          background: #a855f7;
          color: white;
          padding: 0.6rem 1.2rem;
          border-radius: 30px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 1rem;
        }

        .page-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .page-main { padding: 1rem; }
          .page-header { padding: 1rem 1.5rem; }
        }

        .controls-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.6rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
        }

        .controls-grid {
          display: grid;
          grid-template-columns: 1fr 1fr auto;
          gap: 1.2rem;
          align-items: end;
        }

        @media (max-width: 991px) {
          .controls-grid {
            grid-template-columns: 1fr;
          }
          .action-buttons { justify-self: start; margin-top: 1rem; }
        }

        .search-container {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 1.2rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a855f7;
          font-size: 1.1rem;
        }

        .search-input, .filter-select {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          border-radius: 12px;
          border: 2px solid #d8b4fe;
          background: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .filter-select {
          padding: 1rem;
        }

        .search-input:focus, .filter-select:focus {
          outline: none;
          border-color: #a855f7;
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.2);
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
        }

        .refresh-btn, .mark-all-btn {
          padding: 0.8rem 1.4rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          transition: all 0.3s ease;
        }

        .refresh-btn {
          background: #9333ea;
          color: white;
        }

        .refresh-btn:hover {
          background: #7c3aed;
          transform: translateY(-2px);
        }

        .mark-all-btn {
          background: #10b981;
          color: white;
        }

        .mark-all-btn:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .notifications-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.8rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .notifications-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.25);
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .data-table {
          width: 100%;
          min-width: 800px;
          border-collapse: collapse;
          background: white;
        }

        .data-table thead {
          background: #a855f7;
          color: white;
        }

        .data-table th, .data-table td {
          padding: 1.2rem;
          text-align: left;
        }

        .data-table th {
          font-weight: 600;
        }

        .unread-row {
          background: rgba(168, 85, 247, 0.08);
          font-weight: 500;
        }

        .read-row {
          opacity: 0.85;
        }

        .message-cell {
          max-width: 400px;
          word-wrap: break-word;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .unread-dot {
          width: 10px;
          height: 10px;
          background: #a855f7;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .status-badge.read {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.unread {
          background: #fff3cd;
          color: #856404;
        }

        .mark-read-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .mark-read-btn:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        /* Mobile View */
        .mobile-notifications {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .mobile-notification-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 1.4rem;
          border-left: 5px solid #a855f7;
          box-shadow: 0 8px 25px rgba(168, 85, 247, 0.15);
        }

        .mobile-notification-card.unread {
          border-left-color: #9333ea;
          background: rgba(243, 232, 255, 0.9);
        }

        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.8rem;
        }

        .notification-title {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-weight: 600;
          color: #581c87;
        }

        .notification-time {
          font-size: 0.9rem;
          color: #6b46c1;
          opacity: 0.8;
        }

        .notification-message {
          margin: 1rem 0;
          color: #4c1d95;
          line-height: 1.5;
        }

        .notification-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .read-time {
          opacity: 0.8;
        }

        .pagination {
          display: flex;
          justify-content: center;
          gap: 0.8rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        .page-btn {
          padding: 0.7rem 1.2rem;
          border-radius: 12px;
          border: 1px solid #a855f7;
          background: white;
          color: #9333ea;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #a855f7;
          color: white;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #9333ea;
          color: white;
          box-shadow: 0 4px 15px rgba(168, 85, 247, 0.4);
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading-container {
          text-align: center;
          padding: 4rem;
          color: #9333ea;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e9d5ff;
          border-top: 4px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .no-data-card, .no-vip-card {
          text-align: center;
          padding: 4rem;
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          border: 1px solid rgba(168, 85, 247, 0.5);
          color: #9333ea;
        }

        .no-data-card i, .no-vip-card i {
          margin-bottom: 1rem;
          opacity: 0.6;
        }
      `}</style>
    </>
  );
}


// import React, { useEffect, useRef, useState, useMemo } from "react";
// import axios from "axios";
// import { useVipStore } from "../context/VipContext";

// const BASE_URL = process.env.REACT_APP_BASE_URL;
// const ITEMS_PER_PAGE = 10;

// export default function VipNotification() {
//   const { selectedVip } = useVipStore();
//   const vipId = selectedVip?._id || selectedVip?.id;

//   const [notifications, setNotifications] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);

//   /* 🔊 SOUND */
//   const notificationSound = useRef(new Audio("/notify.mp3"));
//   const prevUnreadCount = useRef(0);

//   /*  BACKEND FETCH (MANUAL ONLY) */
//   const fetchNotifications = async () => {
//     if (!vipId) return;

//     try {
//       setLoading(true);
//       const token = localStorage.getItem("vipToken");

//       const res = await axios.get(
//         `${BASE_URL}/api/notification/${vipId}/vip`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       //  NEWEST ON TOP
//       const sortedData = Array.isArray(res.data)
//         ? [...res.data].reverse()
//         : [];

//       setNotifications(sortedData);
//     } catch (err) {
//       console.error("VIP Notification fetch error:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /*  ONLY FETCH ON VIP CHANGE (NO AUTO REFRESH) */
//   useEffect(() => {
//     if (!vipId) return;
//     setNotifications([]);
//     setCurrentPage(1);
//     fetchNotifications();
//   }, [vipId]);

//   /*  UNREAD COUNT */
//   const unreadCount = notifications.filter((n) => !n.read).length;

//   /*  SOUND ONLY WHEN NEW DATA COMES */
//   useEffect(() => {
//     if (unreadCount > prevUnreadCount.current) {
//       notificationSound.current.play().catch(() => {});
//     }
//     prevUnreadCount.current = unreadCount;
//   }, [unreadCount]);

//   /*  SEARCH + FILTER */
//   const filteredData = useMemo(() => {
//     const text = search.trim().toLowerCase();

//     return notifications.filter((n) => {
//       const searchMatch = text
//         ? [n.message, n.vip?.name, n.read ? "read" : "unread"].some((field) =>
//             String(field || "").toLowerCase().includes(text)
//           )
//         : true;

//       const statusMatch = statusFilter
//         ? statusFilter === "unread"
//           ? n.read
//           : !n.read
//         : true;

//       return searchMatch && statusMatch;
//     });
//   }, [notifications, search, statusFilter]);

//   /*  PAGINATION */
//   const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

//   const paginatedData = filteredData.slice(
//     (currentPage - 1) * ITEMS_PER_PAGE,
//     currentPage * ITEMS_PER_PAGE
//   );

//     /* ---------------- DATE FORMAT ---------------- */
//   const formatDate = (dateStr) => {
//     if (!dateStr) return "--";
//     const d = new Date(dateStr);
//     return isNaN(d)
//       ? "--"
//       : d.toLocaleString("en-IN", {
//           day: "2-digit",
//           month: "short",
//           year: "numeric",
//           hour: "2-digit",
//           minute: "2-digit",
//           hour12: true,
//         });
//   };

//   /*  MARK SINGLE READ */
//   const handleMarkAsRead = async (notificationId) => {
//     try {
//       const token = localStorage.getItem("vipToken");

//       await axios.get(`${BASE_URL}/api/notification/read/vip/${notificationId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       notificationSound.current.play().catch(() => {});
//       fetchNotifications();
//     } catch (err) {
//       console.error("VIP mark read failed", err);
//     }
//   };

//   /*  MARK ALL READ */
//   const handleMarkAllAsRead = async () => {
//     const token = localStorage.getItem("vipToken");
//     const unread = notifications.filter((n) => !n.read);

//     try {
//       await Promise.all(
//         unread.map((n) =>
//           axios.get(
//             `${BASE_URL}/api/notification/read/all/vip/${id}`,
//             { headers: { Authorization: `Bearer ${token}` } }
//           )
//         )
//       );

//       notificationSound.current.play().catch(() => {});
//       fetchNotifications();
//     } catch (err) {
//       console.error("VIP mark all read failed", err);
//     }
//   };

//   /*  MANUAL REFRESH BUTTON FUNCTION */
//   const handleRefresh = () => {
//     fetchNotifications();
//   };

//   if (!vipId) {
//     return (
//       <div style={styles.container}>
//         <div style={styles.noData}>No VIP Selected</div>
//       </div>
//     );
//   }

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>
//         VIP Notification Management ({unreadCount})
//       </h2>

//       {/*  FILTER ROW */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             placeholder="Search by message or Vip..."
//             style={styles.input}
//           />
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Status</label>
//           <select
//             value={statusFilter}
//             onChange={(e) => setStatusFilter(e.target.value)}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             <option value="read">Read</option>
//             <option value="unread">Unread</option>
//           </select>
//         </div>

//         {/*  NEW REFRESH BUTTON */}
//         <div style={{ alignSelf: "end", display: "flex", gap: 10 }}>
//           <button style={styles.refreshBtn} onClick={handleRefresh}>
//             Refresh
//           </button>

//           <button style={styles.acceptBtn} onClick={handleMarkAllAsRead}>
//             Mark All Read
//           </button>
//         </div>
//       </div>

//       {/*  TABLE */}
//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : paginatedData.length === 0 ? (
//           <div style={styles.noData}>No Notifications Found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>#</th>
//                 <th style={styles.th}>Message</th>
//                 <th style={styles.th}>Receive Time</th>
//                 <th style={styles.th}>Read Time</th>
//                 <th style={styles.th}>Action</th>
//               </tr>
//             </thead>

//             <tbody>
//               {paginatedData.map((item, index) => {
//                 const statusStyle = item.read
//                   ? styles.statusActive
//                   : styles.statusOther;

//                 return (
//                   <tr key={item.notificationId} style={styles.row}>
//                     <td style={styles.td}>
//                       {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
//                     </td>
//                     <td style={styles.td}>{item.notificationMessage}</td>
//                      <td style={styles.td}>
//                     {formatDate(item.notificationAssignTime)}
//                   </td>
//                   <td style={styles.td}>
//                     {formatDate(item.notificationReadTime)}
//                   </td>
//                   <td style={styles.td}>
//                     {item.notificationStatus ? (
//                       <span style={{ color: "#888", fontWeight: 600 }}>
//                         Completed
//                       </span>
//                     ) : (
//                       <button
//                         style={styles.acceptBtn}
//                         onClick={() =>
//                           handleMarkAsRead(item.notificationId)
//                         }
//                       >
//                         Mark Read
//                       </button>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>

//       {/*  PAGINATION */}
//       {totalPages > 1 && (
//         <div style={{ display: "flex", justifyContent: "center", marginTop: 20, gap: 8 }}>
//           <button
//             style={styles.pageBtn}
//             disabled={currentPage === 1}
//             onClick={() => setCurrentPage((p) => p - 1)}
//           >
//             Prev
//           </button>

//           {[...Array(totalPages)].map((_, i) => (
//             <button
//               key={i}
//               style={{
//                 ...styles.pageBtn,
//                 background: currentPage === i + 1 ? "#1a73e8" : "#fff",
//                 color: currentPage === i + 1 ? "#fff" : "#000",
//               }}
//               onClick={() => setCurrentPage(i + 1)}
//             >
//               {i + 1}
//             </button>
//           ))}

//           <button
//             style={styles.pageBtn}
//             disabled={currentPage === totalPages}
//             onClick={() => setCurrentPage((p) => p + 1)}
//           >
//             Next
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// /*  STYLES */
// const styles = {
//   container: { padding: 30, background: "#fff" },
//   title: { fontSize: 30, fontWeight: 800, marginBottom: 25, color: "#1967d2" },

//   filterRow: { display: "flex", gap: 20, marginBottom: 25 },
//   filterCard: {
//     flex: 1,
//     padding: 18,
//     borderRadius: 12,
//     background: "#fff",
//     boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
//   },

//   filterLabel: { fontSize: 15, fontWeight: 600, marginBottom: 6 },

//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   input: {
//     width: "100%",
//     padding: 12,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//     outline: "none",
//   },

//   card: {
//     background: "#fff",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//   },

//   table: { width: "100%", borderCollapse: "collapse" },

//   th: {
//     background: "#f4f4f6",
//     padding: "16px 10px",
//     textAlign: "left",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//   },

//   row: { height: 62 },
//   td: { padding: "14px 10px", borderBottom: "1px solid #eee" },

//   statusActive: {
//     background: "#e6ffe6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },

//   statusOther: {
//     background: "#fff8e1",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//     fontWeight: 600,
//   },

//   acceptBtn: {
//     background: "#2e7d32",
//     color: "#fff",
//     border: "none",
//     padding: "7px 14px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   refreshBtn: {
//     background: "#1a73e8",
//     color: "#fff",
//     border: "none",
//     padding: "7px 16px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   pageBtn: {
//     padding: "6px 14px",
//     border: "1px solid #ccc",
//     borderRadius: 6,
//     cursor: "pointer",
//   },

//   noData: {
//     textAlign: "center",
//     padding: 40,
//     fontSize: 18,
//     color: "#777",
//   },
// };
